import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { 
  ThumbsUp, 
  ThumbsDown, 
  MessageCircle, 
  TrendingUp, 
  TrendingDown,
  CheckCircle,
  XCircle,
  PlusCircle,
  Lightbulb,
  Vote,
  Zap,
  Users
} from "lucide-react";
import { stripMarkdownFormatting } from "@/lib/stripMarkdown";

interface SprintReviewDashboardProps {
  sprintId: string;
  userId: string;
}

interface InformContribution {
  id: string;
  contributor_id: string;
  content_type: string;
  content_reference: string;
  ai_summary: string | null;
  key_concepts: any;
}

interface DecisionContribution {
  id: string;
  decision_text: string;
  rationale: string;
  is_selected: boolean;
  ai_confidence_score: number | null;
}

interface ExecuteContribution {
  id: string;
  contributor_id: string;
  contribution_text: string;
  prompt_used: string | null;
  ai_output: string | null;
  contributor_rating: number | null;
}

interface Assessment {
  assessor_id: string;
  rating_action: string;
  feedback_text: string | null;
}

const stageActions = {
  inform: [
    { action: 'valuable', label: 'Valuable', icon: ThumbsUp, score: 3, color: 'text-green-600', description: 'Strong foundational input' },
    { action: 'enhance', label: 'Enhance', icon: TrendingUp, score: 1, color: 'text-blue-600', description: 'Good, could be expanded' },
    { action: 'reduce', label: 'Reduce', icon: TrendingDown, score: 0, color: 'text-yellow-600', description: 'Too much detail' },
    { action: 'needs_context', label: 'Needs Context', icon: MessageCircle, score: -1, color: 'text-orange-600', description: 'Unclear or incomplete' },
  ],
  decide: [
    { action: 'strategic_vote', label: 'Strategic Vote', icon: Vote, score: 3, color: 'text-purple-600', description: 'Aligns with objectives' },
    { action: 'alternative', label: 'Consider Alternative', icon: Lightbulb, score: 1, color: 'text-blue-600', description: 'Worth exploring other options' },
    { action: 'needs_discussion', label: 'Needs Discussion', icon: MessageCircle, score: 0, color: 'text-orange-600', description: 'Requires more debate' },
    { action: 'misaligned', label: 'Misaligned', icon: XCircle, score: -1, color: 'text-red-600', description: 'Off objectives' },
  ],
  execute: [
    { action: 'accept', label: 'Accept', icon: CheckCircle, score: 3, color: 'text-green-600', description: 'Ready to use' },
    { action: 'build_on', label: 'Build On', icon: PlusCircle, score: 2, color: 'text-blue-600', description: 'Great foundation to extend' },
    { action: 'enhance', label: 'Enhance', icon: TrendingUp, score: 1, color: 'text-yellow-600', description: 'Needs improvement' },
    { action: 'rethink', label: 'Rethink', icon: XCircle, score: -1, color: 'text-red-600', description: 'Fundamental issues' },
  ]
};

export const SprintReviewDashboard = ({ sprintId, userId }: SprintReviewDashboardProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('execute');
  
  const [informContributions, setInformContributions] = useState<InformContribution[]>([]);
  const [decisionContributions, setDecisionContributions] = useState<DecisionContribution[]>([]);
  const [executeContributions, setExecuteContributions] = useState<ExecuteContribution[]>([]);
  const [contributorProfiles, setContributorProfiles] = useState<Record<string, { display_name: string | null }>>({});
  
  const [selectedRatings, setSelectedRatings] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<Record<string, string>>({});
  const [existingAssessments, setExistingAssessments] = useState<Record<string, Assessment[]>>({});

  useEffect(() => {
    loadAllContributions();
  }, [sprintId]);

  const loadAllContributions = async () => {
    try {
      setLoading(true);

      // Load Inform stage contributions
      const { data: informData, error: informError } = await supabase
        .from('sprint_inform_content')
        .select('id, contributor_id, content_type, content_reference, ai_summary, key_concepts')
        .eq('sprint_id', sprintId);

      if (informError) throw informError;

      // Load Decide stage contributions
      const { data: decideData, error: decideError } = await supabase
        .from('sprint_decisions')
        .select('*')
        .eq('sprint_id', sprintId);

      if (decideError) throw decideError;

      // Load Execute stage contributions
      const { data: executeData, error: executeError } = await supabase
        .from('sprint_contributions')
        .select('id, contributor_id, contribution_text, prompt_used, ai_output, contributor_rating')
        .eq('sprint_id', sprintId)
        .eq('stage', 'execute');

      if (executeError) throw executeError;

      // Get unique contributor IDs
      const contributorIds = [
        ...(informData || []).map(c => c.contributor_id),
        ...(executeData || []).map(c => c.contributor_id),
      ];
      const uniqueContributorIds = [...new Set(contributorIds)];

      // Load contributor profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, display_name')
        .in('id', uniqueContributorIds);

      if (profilesError) throw profilesError;

      // Create profile lookup
      const profileLookup = (profilesData || []).reduce((acc, profile) => {
        acc[profile.id] = { display_name: profile.display_name };
        return acc;
      }, {} as Record<string, { display_name: string | null }>);

      setContributorProfiles(profileLookup);

      // Load existing assessments
      const allContributionIds = [
        ...(informData || []).map(c => c.id),
        ...(decideData || []).map(c => c.id),
        ...(executeData || []).map(c => c.id),
      ];

      const { data: assessmentsData, error: assessmentsError } = await supabase
        .from('sprint_assessments')
        .select('*')
        .in('contribution_id', allContributionIds);

      if (assessmentsError) throw assessmentsError;

      // Group assessments by contribution_id
      const assessmentsByContribution = (assessmentsData || []).reduce((acc, assessment) => {
        if (!acc[assessment.contribution_id]) {
          acc[assessment.contribution_id] = [];
        }
        acc[assessment.contribution_id].push(assessment);
        return acc;
      }, {} as Record<string, Assessment[]>);

      setInformContributions((informData || []) as InformContribution[]);
      setDecisionContributions(decideData || []);
      setExecuteContributions((executeData || []) as ExecuteContribution[]);
      setExistingAssessments(assessmentsByContribution);

      // Pre-populate selected ratings for user's existing assessments
      const userRatings: Record<string, string> = {};
      (assessmentsData || []).forEach(assessment => {
        if (assessment.assessor_id === userId) {
          userRatings[assessment.contribution_id] = assessment.rating_action;
        }
      });
      setSelectedRatings(userRatings);

    } catch (error: any) {
      toast({
        title: "Failed to load contributions",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRate = async (contributionId: string, action: string, stage: 'inform' | 'decide' | 'execute') => {
    try {
      const stageConfig = stageActions[stage];
      const ratingConfig = stageConfig.find(r => r.action === action);
      
      if (!ratingConfig) {
        toast({
          title: "Invalid Rating",
          description: "Please select a valid rating option.",
          variant: "destructive",
        });
        return;
      }

      // Save assessment
      const { error: assessError } = await supabase
        .from('sprint_assessments')
        .upsert({
          contribution_id: contributionId,
          assessor_id: userId,
          rating_action: action,
          feedback_text: feedback[contributionId] || null
        }, {
          onConflict: 'contribution_id,assessor_id'
        });

      if (assessError) throw assessError;

      // Fetch all ratings for this contribution to calculate average
      const { data: allAssessments, error: fetchError } = await supabase
        .from('sprint_assessments')
        .select('rating_action')
        .eq('contribution_id', contributionId);

      if (fetchError) throw fetchError;

      // Calculate average rating from all assessments
      const ratings = (allAssessments || []).map(assessment => {
        const config = stageConfig.find(r => r.action === assessment.rating_action);
        return config?.score || 0;
      });
      
      const averageRating = ratings.length > 0 
        ? ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length 
        : ratingConfig.score;

      // Update the contribution with the average numerical rating
      if (stage === 'execute') {
        await supabase
          .from('sprint_contributions')
          .update({ contributor_rating: averageRating })
          .eq('id', contributionId);
      }

      setSelectedRatings(prev => ({ ...prev, [contributionId]: action }));
      
      toast({ 
        title: "Rating Submitted",
        description: `You rated this contribution as "${ratingConfig.label}".`
      });

      // Reload to show updated assessments
      loadAllContributions();

    } catch (error: any) {
      toast({
        title: "Failed to submit rating",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const renderParticipantBadge = (contributorId: string) => {
    const profile = contributorProfiles[contributorId];
    const displayName = profile?.display_name;
    const initials = displayName ? displayName.split(' ').map(n => n[0]).join('').toUpperCase() : '?';
    const isCurrentUser = contributorId === userId;
    
    return (
      <div className="flex items-center gap-2">
        <Avatar className="h-6 w-6">
          <AvatarFallback className="text-xs">{initials}</AvatarFallback>
        </Avatar>
        <span className="text-sm font-medium">
          {isCurrentUser ? 'You' : (displayName || 'Anonymous')}
        </span>
      </div>
    );
  };

  const renderAssessmentSummary = (contributionId: string) => {
    const assessments = existingAssessments[contributionId] || [];
    if (assessments.length === 0) return null;

    const actionCounts = assessments.reduce((acc, assessment) => {
      acc[assessment.rating_action] = (acc[assessment.rating_action] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return (
      <div className="flex items-center gap-2 flex-wrap">
        {Object.entries(actionCounts).map(([action, count]) => (
          <Badge key={action} variant="secondary" className="text-xs">
            {action.replace('_', ' ')}: {count}
          </Badge>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div>
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            <Users className="h-5 w-5" />
            Comprehensive Sprint Review
          </h3>
          <p className="text-muted-foreground">
            Review all participant contributions across all stages and provide feedback.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="inform" className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4" />
              Inform ({informContributions.length})
            </TabsTrigger>
            <TabsTrigger value="decide" className="flex items-center gap-2">
              <Vote className="h-4 w-4" />
              Decide ({decisionContributions.length})
            </TabsTrigger>
            <TabsTrigger value="execute" className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Execute ({executeContributions.length})
            </TabsTrigger>
          </TabsList>

          {/* Inform Stage Tab */}
          <TabsContent value="inform" className="space-y-4 mt-6">
            {informContributions.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">No Inform stage contributions yet.</p>
              </Card>
            ) : (
              informContributions.map((contribution) => {
                const isOwnContribution = contribution.contributor_id === userId;
                const selectedRating = selectedRatings[contribution.id];

                return (
                  <Card key={contribution.id} className="p-4 space-y-4">
                    <div className="flex items-start justify-between">
                      {renderParticipantBadge(contribution.contributor_id)}
                      <Badge variant="outline">{contribution.content_type}</Badge>
                    </div>

                    <div className="space-y-2">
                      <div className="text-sm font-medium text-muted-foreground">Content Reference:</div>
                      <div className="bg-muted/30 rounded-lg p-3 text-sm break-words">
                        {contribution.content_reference}
                      </div>
                    </div>

                    {contribution.ai_summary && (
                      <div className="space-y-2">
                        <div className="text-sm font-medium text-muted-foreground">AI Summary:</div>
                        <div className="bg-muted/30 rounded-lg p-3 text-sm">
                          {contribution.ai_summary}
                        </div>
                      </div>
                    )}

                    {contribution.key_concepts && Array.isArray(contribution.key_concepts) && contribution.key_concepts.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {contribution.key_concepts.map((concept: any, idx: number) => (
                          <Badge key={idx} variant="secondary">
                            {typeof concept === 'string' ? concept : concept.concept}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {renderAssessmentSummary(contribution.id)}

                    {!isOwnContribution && (
                      <>
                        <div className="flex items-center gap-2 flex-wrap">
                          {stageActions.inform.map(({ action, icon: Icon, label, color, description }) => (
                            <Button
                              key={action}
                              variant="outline"
                              size="sm"
                              onClick={() => handleRate(contribution.id, action, 'inform')}
                              disabled={selectedRating === action}
                              className={`flex items-center gap-2 ${
                                selectedRating === action ? 'ring-2 ring-primary' : ''
                              }`}
                              title={description}
                            >
                              <Icon className={`h-4 w-4 ${color}`} />
                              <span className={color}>{label}</span>
                            </Button>
                          ))}
                        </div>

                        <Textarea
                          value={feedback[contribution.id] || ''}
                          onChange={(e) => setFeedback(prev => ({ 
                            ...prev, 
                            [contribution.id]: e.target.value 
                          }))}
                          placeholder="Optional: Add your feedback or comments..."
                          rows={2}
                        />
                      </>
                    )}
                  </Card>
                );
              })
            )}
          </TabsContent>

          {/* Decide Stage Tab */}
          <TabsContent value="decide" className="space-y-4 mt-6">
            {decisionContributions.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">No Decide stage contributions yet.</p>
              </Card>
            ) : (
              decisionContributions.map((contribution) => {
                const selectedRating = selectedRatings[contribution.id];

                return (
                  <Card key={contribution.id} className="p-4 space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <Vote className="h-5 w-5 text-purple-600" />
                        <span className="font-semibold">Decision Option</span>
                      </div>
                      {contribution.is_selected && (
                        <Badge className="bg-green-600">Selected</Badge>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="text-sm font-medium text-muted-foreground">Decision:</div>
                      <div className="bg-muted/30 rounded-lg p-3 text-sm">
                        {contribution.decision_text}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="text-sm font-medium text-muted-foreground">Rationale:</div>
                      <div className="bg-muted/30 rounded-lg p-3 text-sm">
                        {contribution.rationale}
                      </div>
                    </div>

                    {contribution.ai_confidence_score !== null && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">AI Confidence:</span>
                        <Badge variant="outline">{(contribution.ai_confidence_score * 100).toFixed(0)}%</Badge>
                      </div>
                    )}

                    {renderAssessmentSummary(contribution.id)}

                    <>
                      <div className="flex items-center gap-2 flex-wrap">
                        {stageActions.decide.map(({ action, icon: Icon, label, color, description }) => (
                          <Button
                            key={action}
                            variant="outline"
                            size="sm"
                            onClick={() => handleRate(contribution.id, action, 'decide')}
                            disabled={selectedRating === action}
                            className={`flex items-center gap-2 ${
                              selectedRating === action ? 'ring-2 ring-primary' : ''
                            }`}
                            title={description}
                          >
                            <Icon className={`h-4 w-4 ${color}`} />
                            <span className={color}>{label}</span>
                          </Button>
                        ))}
                      </div>

                      <Textarea
                        value={feedback[contribution.id] || ''}
                        onChange={(e) => setFeedback(prev => ({ 
                          ...prev, 
                          [contribution.id]: e.target.value 
                        }))}
                        placeholder="Optional: Add your feedback or comments..."
                        rows={2}
                      />
                    </>
                  </Card>
                );
              })
            )}
          </TabsContent>

          {/* Execute Stage Tab */}
          <TabsContent value="execute" className="space-y-4 mt-6">
            {executeContributions.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">No Execute stage contributions yet.</p>
              </Card>
            ) : (
              executeContributions.map((contribution) => {
                const isOwnContribution = contribution.contributor_id === userId;
                const selectedRating = selectedRatings[contribution.id];

                return (
                  <Card key={contribution.id} className="p-4 space-y-4">
                    <div className="flex items-start justify-between">
                      {renderParticipantBadge(contribution.contributor_id)}
                      {contribution.contributor_rating !== null && (
                        <Badge variant="outline">
                          Rating: {contribution.contributor_rating.toFixed(1)}
                        </Badge>
                      )}
                    </div>

                    {contribution.prompt_used && (
                      <div className="space-y-2">
                        <div className="text-sm font-medium text-muted-foreground">Prompt Used:</div>
                        <div className="bg-muted/30 rounded-lg p-3 text-sm">
                          {contribution.prompt_used}
                        </div>
                      </div>
                    )}

                    {contribution.ai_output && (
                      <div className="space-y-2">
                        <div className="text-sm font-medium text-muted-foreground">AI Output:</div>
                        <div className="bg-muted/30 rounded-lg p-3 max-h-[300px] overflow-y-auto">
                          <div 
                            className="text-sm whitespace-pre-wrap"
                            dangerouslySetInnerHTML={{ __html: stripMarkdownFormatting(contribution.ai_output) }}
                          />
                        </div>
                      </div>
                    )}

                    {renderAssessmentSummary(contribution.id)}

                    {!isOwnContribution && (
                      <>
                        <div className="flex items-center gap-2 flex-wrap">
                          {stageActions.execute.map(({ action, icon: Icon, label, color, description }) => (
                            <Button
                              key={action}
                              variant="outline"
                              size="sm"
                              onClick={() => handleRate(contribution.id, action, 'execute')}
                              disabled={selectedRating === action}
                              className={`flex items-center gap-2 ${
                                selectedRating === action ? 'ring-2 ring-primary' : ''
                              }`}
                              title={description}
                            >
                              <Icon className={`h-4 w-4 ${color}`} />
                              <span className={color}>{label}</span>
                            </Button>
                          ))}
                        </div>

                        <Textarea
                          value={feedback[contribution.id] || ''}
                          onChange={(e) => setFeedback(prev => ({ 
                            ...prev, 
                            [contribution.id]: e.target.value 
                          }))}
                          placeholder="Optional: Add your feedback or comments..."
                          rows={2}
                        />
                      </>
                    )}
                  </Card>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Card>
  );
};